<script>
import VTurbineSearch from "@/components/VTurbineSearch.vue";
import UserService from "@/services/UserService";
import TurbineService from "@/services/TurbineService";
import TurbineStopService from "../../services/TurbineStopService";
import Cloner from "@/libraries/Cloner";
import {
    CaseCategory,
    CaseStatus,
    CaseExtraTasks,
    CaseFinalizedTypes
} from "@/constants/TurbineStops.const";
import dayjs from "dayjs";
import { mapGetters } from "vuex";

// The employees we'll list on this page in connection with handling turbine cases:
const userDepartmentId = [16, 32];
// Today's date:
const isoDate = dayjs().format("YYYY-MM-DD");

export default {
    props: {
        context: {},
        scope: {
            type: String,
            default: ""
        }
    },
    components: { VTurbineSearch },
    data() {
        return {
            loading: true,
            saving: false,
            editing: false,
            errors: {},
            turbineIdSelected: false,
            entity: {
                id: null,
                so_nr: null,
                entry_category: 1,
                sl_turbine_id: null,
                customer_type: null,
                user_id: null,
                supervisor_user_id: null,
                start_date: isoDate,
                exp_repair_date: null,
                error_code: null,
                extra_task: null,
                error_text: null,
                must_be_turned: 1,
                last_turned_date: null,
                car: null,
                status: 0,
                comments: null,
                finalized_user_id: null,
                finalized_date: null,
                is_contacted: null,
                is_driving: null,
                finalized_type: null,
                sl_turbine: [
                    {
                        id: null,
                        source: "ext",
                        serial_number: null,
                        name: null,
                        gsrn: null,
                        address: null,
                        coordinates_url: null,
                        wm_type: null
                    }
                ]
            },
            caseCategories: CaseCategory,
            caseStatuses: CaseStatus,
            extraTasks: CaseExtraTasks,
            users: []
        };
    },
    watch: {
        status: function(newV) {
            if (parseInt(newV) == 9 && this.entity.finalized_date === null) {
                this.entity.finalized_date = isoDate;
            } else if (parseInt(newV) != 9) {
                this.entity.finalized_date = null;
                this.entity.finalized_user_id = null;
                this.entity.finalized_type = null;
            }
        }
    },
    async created() {
        if (!Object.keys(this.context).length) {
            this.editing = false;
        } else {
            this.editing = true;
            this.turbineIdSelected = true;
            await this.fetchCase();
        }

        this.loading = false;
    },
    async mounted() {
        await this.getUsers();
    },
    computed: {
        ...mapGetters("auth", ["getMyId"]),
        status() {
            return this.entity.status;
        },
        turbineIsEditable: function() {
            // When editing ext allow
            if (this.editing && this.entity.sl_turbine[0].source === "ext") {
                return true;
            }

            // When creating ext allow
            if (!this.editing && this.entity.sl_turbine[0].id === null) {
                return true;
            }

            // Block otherwise
            return false;
        },
        hideOnScope() {
            if (this.scope !== "") {
                return false;
            } else {
                return true;
            }
        },
        finalized_types() {
            return CaseFinalizedTypes;
        }
    },
    methods: {
        /**
         * Fetch a single case and remodel inside sl_turbine for vueformulate groups
         */
        async fetchCase() {
            try {
                const { data } = await TurbineStopService.GETS(this.context.id);
                this.entity = data.data;
                this.entity.sl_turbine = [this.entity.sl_turbine];
            } catch (e) {
                console.log("fetch error", e);
            }
        },
        // Get those users/employees who handle turbine stop cases.
        async getUsers() {
            try {
                const { data } = await UserService.dropdown({
                    department_id: userDepartmentId
                });
                this.users = data.data;
            } catch (e) {
                console.log("Fetch error:", e);
            }
        },
        // Get turbine by ID.
        async getTurbine(turbineId) {
            try {
                const { data } = await TurbineService.GETS(turbineId);
                return data.data;
            } catch (e) {
                console.log("Fetch error:", e);
            }
        },
        /**
         * Save form
         */
        async saveForm() {
            this.saving = true;
            try {
                const cloned = Cloner.clone(this.entity);
                cloned.sl_turbine = cloned.sl_turbine[0];

                if (parseInt(cloned.sl_turbine.id) > 0) {
                    // Turbine is already in the DB
                    if (this.editing) {
                        if (cloned.sl_turbine.source != "ext") {
                            delete cloned.sl_turbine;
                        }
                    } else {
                        // Creation
                        delete cloned.sl_turbine;
                    }
                } else {
                    delete cloned.sl_turbine_id;
                }

                if (this.editing) {
                    await TurbineStopService.PUT(cloned);
                } else {
                    await TurbineStopService.POST(cloned);
                }
                this.$emit("close-reload");
            } catch (e) {
                this.errors = e.response.data.errors;
            }

            this.saving = false;
        },
        /**
         * Select turbine from the dropdown
         * @param turbine
         */
        async selectTurbine(turbine) {
            const turbineInfo = await this.getTurbine(turbine.value);
            this.entity.sl_turbine_id = turbineInfo.id;
            this.entity.sl_turbine[0] = Cloner.pickClone(
                turbineInfo,
                Object.keys(this.entity.sl_turbine[0])
            );
            this.turbineIdSelected = true;

            // Pre-fill user information for creation
            if (!this.editing) {
                this.entity.supervisor_user_id = this.getMyId;
                this.entity.user_id = this.getMyId;
            }
        },
        hasError(field) {
            if (this.errors?.["sl_turbine"]?.[field]) {
                return this.errors["sl_turbine"][field].join(" ");
            } else {
                return null;
            }
        },
        showScope(localScope) {
            if (this.scope === "") {
                return true;
            } else if (localScope == this.scope) {
                return true;
            } else {
                return false;
            }
        }
    }
};
</script>

<template>
    <modal
        size="lg"
        :show="true"
        @close="$emit('close')"
        class="budget-modal"
        :loading="loading"
    >
        <template slot="header">
            <template v-if="editing">
                {{ $t("service.edit_case") }}
            </template>
            <template v-else>
                {{ $t("service.create_case") }}
            </template>
        </template>

        <template slot="default">
            <template v-if="!turbineIdSelected">
                <label>
                    {{ $t("service.select_turbine") }}
                </label>
                <v-turbine-search
                    @choose-item="selectTurbine"
                    :select-multiple="false"
                />
                <base-button
                    type="light"
                    :outline="true"
                    class="mt-4"
                    @click="turbineIdSelected = true"
                    >{{ $t("service.not_on_list") }}</base-button
                >

                <div class="text-right">
                    <base-button
                        type="secondary"
                        :outline="true"
                        @click="$emit('close')"
                        >{{ $t("cancel") }}</base-button
                    >
                </div>
            </template>
            <template v-else>
                <h3 class="mb-5" v-show="hideOnScope">
                    {{ $t("service.turbine_info") }}
                </h3>
                <FormulateForm v-model="entity" :errors="errors">
                    <div v-show="hideOnScope">
                        <FormulateInput
                            type="group"
                            name="sl_turbine"
                            :repeatable="false"
                        >
                            <div class="row">
                                <div class="col-12">
                                    <FormulateInput
                                        name="serial_number"
                                        type="text"
                                        :disabled="!turbineIsEditable"
                                        :label="
                                            $t('service.serial_number_long')
                                        "
                                        :placeholder="
                                            $t('service.serial_number_long')
                                        "
                                        :validation-name="
                                            $t('service.serial_number_long')
                                        "
                                        :error="hasError('serial_number')"
                                    />
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-12">
                                    <FormulateInput
                                        name="name"
                                        type="text"
                                        :disabled="!turbineIsEditable"
                                        :label="$t('service.turbine_name')"
                                        :placeholder="
                                            $t('service.turbine_name')
                                        "
                                        :validation-name="
                                            $t('service.turbine_name')
                                        "
                                        :error="hasError('name')"
                                    />
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-12">
                                    <FormulateInput
                                        name="gsrn"
                                        type="text"
                                        :disabled="!turbineIsEditable"
                                        :label="$t('service.gsrn')"
                                        :placeholder="$t('service.gsrn')"
                                        :validation-name="$t('service.gsrn')"
                                    />
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-12">
                                    <FormulateInput
                                        name="address"
                                        type="text"
                                        :disabled="!turbineIsEditable"
                                        :label="$t('service.address')"
                                        :placeholder="$t('service.address')"
                                        :validation-name="$t('service.address')"
                                        :error="hasError('address')"
                                    />
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-12">
                                    <FormulateInput
                                        name="coordinates_url"
                                        type="text"
                                        :disabled="!turbineIsEditable"
                                        :label="$t('service.location_long')"
                                        :placeholder="
                                            $t('service.location_long')
                                        "
                                        :validation-name="
                                            $t('service.location_long')
                                        "
                                    />
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-12">
                                    <FormulateInput
                                        name="wm_type"
                                        type="text"
                                        :disabled="!turbineIsEditable"
                                        :label="$t('service.type_long')"
                                        :placeholder="$t('service.type_long')"
                                        :validation-name="
                                            $t('service.type_long')
                                        "
                                        :error="hasError('wm_type')"
                                    />
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-12">
                                    <FormulateInput
                                        name="source"
                                        type="select"
                                        :options="{
                                            ext: $t('service.external'),
                                            mes: $t('service.external_2'),
                                            meg: $t('service.internal')
                                        }"
                                        :disabled="true"
                                        :label="$t('service.source')"
                                        :placeholder="$t('service.source')"
                                        :validation-name="$t('service.source')"
                                    />
                                </div>
                            </div>
                        </FormulateInput>
                    </div>

                    <h3 class="'mb-5 mt-5'" v-show="hideOnScope">
                        {{ $t("service.turbine_stop_case") }}
                    </h3>
                    <div class="row" v-show="showScope('so_nr')">
                        <div class="col-12">
                            <FormulateInput
                                name="so_nr"
                                type="text"
                                :label="$t('service.so_nr')"
                                :placeholder="$t('service.so_nr')"
                                :validation-name="$t('service.so_nr')"
                            />
                        </div>
                    </div>
                    <div class="row" v-show="showScope('is_contacted')">
                        <div class="col-12">
                            <FormulateInput
                                name="is_contacted"
                                type="fswitch"
                                :label="$t('service.is_contacted')"
                                :placeholder="$t('service.is_contacted')"
                                :validation-name="$t('service.is_contacted')"
                            />
                        </div>
                    </div>
                    <div class="row" v-show="showScope('is_driving')">
                        <div class="col-12">
                            <FormulateInput
                                name="is_driving"
                                type="fswitch"
                                :label="$t('service.is_driving')"
                                :placeholder="$t('service.is_driving')"
                                :validation-name="$t('service.is_driving')"
                            />
                        </div>
                    </div>
                    <div class="row" v-show="showScope('entry_category')">
                        <div class="col-12">
                            <FormulateInput
                                name="entry_category"
                                type="select"
                                :options="caseCategories"
                                :label="$t('service.case_category')"
                                :placeholder="$t('service.case_category')"
                                :validation-name="$t('service.case_category')"
                            />
                        </div>
                    </div>
                    <div class="row" v-show="showScope('user_id')">
                        <div class="col-12">
                            <FormulateInput
                                name="user_id"
                                type="select"
                                :options="users"
                                :label="$t('service.created_by')"
                                :placeholder="$t('service.created_by')"
                                :validation-name="$t('service.created_by')"
                            />
                        </div>
                    </div>
                    <div class="row" v-show="showScope('supervisor_user_id')">
                        <div class="col-12">
                            <FormulateInput
                                name="supervisor_user_id"
                                type="select"
                                :options="users"
                                :label="$t('service.supervisor')"
                                :placeholder="$t('service.supervisor')"
                                :validation-name="$t('service.supervisor')"
                            />
                        </div>
                    </div>
                    <div class="row" v-show="showScope('extra_task')">
                        <div class="col-12">
                            <FormulateInput
                                name="extra_task"
                                type="select"
                                :options="extraTasks"
                                :label="$t('service.extra_task')"
                                :placeholder="$t('service.extra_task_plc')"
                                :validation-name="$t('service.extra_task')"
                            />
                        </div>
                    </div>
                    <div class="row" v-show="showScope('start_date')">
                        <div class="col-12">
                            <FormulateInput
                                name="start_date"
                                type="date"
                                :label="$t('service.start_date_long')"
                                :placeholder="$t('service.start_date_long')"
                                :validation-name="$t('service.start_date_long')"
                            />
                        </div>
                    </div>
                    <div class="row" v-show="showScope('exp_repair_date')">
                        <div class="col-12">
                            <FormulateInput
                                name="exp_repair_date"
                                type="date"
                                :label="$t('service.exp_repair_date')"
                                :placeholder="$t('service.exp_repair_date')"
                                :validation-name="$t('service.exp_repair_date')"
                            />
                        </div>
                    </div>
                    <div class="row" v-show="showScope('error_code')">
                        <div class="col-12">
                            <FormulateInput
                                name="error_code"
                                type="text"
                                :label="$t('service.error_code')"
                                :placeholder="$t('service.error_code')"
                                :validation-name="$t('service.error_code')"
                            />
                        </div>
                    </div>
                    <div class="row" v-show="showScope('error_text')">
                        <div class="col-12">
                            <FormulateInput
                                name="error_text"
                                type="textarea"
                                rows="5"
                                :label="$t('service.error_text')"
                                :placeholder="$t('service.error_text')"
                                :validation-name="$t('service.error_text')"
                            />
                        </div>
                    </div>
                    <div v-show="showScope('must_be_turned')">
                        <div class="row">
                            <div class="col-12">
                                <FormulateInput
                                    name="must_be_turned"
                                    type="fswitch"
                                    :label="$t('service.must_be_turned')"
                                    :placeholder="$t('service.must_be_turned')"
                                    :validation-name="
                                        $t('service.must_be_turned')
                                    "
                                />
                            </div>
                        </div>
                        <div class="row" v-if="entity.must_be_turned">
                            <div class="col-12">
                                <FormulateInput
                                    name="last_turned_date"
                                    type="date"
                                    :label="$t('service.last_turned_date')"
                                    :placeholder="
                                        $t('service.last_turned_date')
                                    "
                                    :validation-name="
                                        $t('service.last_turned_date')
                                    "
                                />
                            </div>
                        </div>
                    </div>
                    <div class="row" v-show="showScope('car')">
                        <div class="col-12">
                            <FormulateInput
                                name="car"
                                type="text"
                                :label="$t('service.car')"
                                :placeholder="$t('service.car')"
                                :validation-name="$t('service.car')"
                            />
                        </div>
                    </div>
                    <div class="row" v-show="showScope('status')">
                        <div class="col-12">
                            <FormulateInput
                                name="status"
                                type="select"
                                :options="caseStatuses"
                                :label="$t('service.status')"
                                :placeholder="$t('service.status')"
                                :validation-name="$t('service.status')"
                            />
                        </div>
                    </div>
                    <!-- If the case is marked "done": -->
                    <div v-show="entity.status == 9">
                        <div class="finalized-elems">
                            <div class="row">
                                <div class="col-12">
                                    <FormulateInput
                                        name="finalized_user_id"
                                        type="select"
                                        :options="users"
                                        :label="$t('service.finalized_user')"
                                        :placeholder="
                                            $t('service.finalized_user')
                                        "
                                        :validation-name="
                                            $t('service.finalized_user')
                                        "
                                    />
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-12">
                                    <FormulateInput
                                        name="finalized_date"
                                        type="date"
                                        :label="$t('service.finalized_date')"
                                        :placeholder="
                                            $t('service.finalized_date')
                                        "
                                        :validation-name="
                                            $t('service.finalized_date')
                                        "
                                    />
                                </div>
                            </div>

                            <div class="row">
                                <div class="col-12">
                                    <FormulateInput
                                        name="finalized_type"
                                        type="select"
                                        :label="$t('service.finalized_type')"
                                        :options="finalized_types"
                                        :placeholder="
                                            $t('service.select_finalized_type')
                                        "
                                        :validation-name="
                                            $t('service.finalized_type')
                                        "
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="row" v-show="showScope('comments')">
                        <div class="col-12">
                            <FormulateInput
                                name="comments"
                                type="textarea"
                                rows="5"
                                :label="$t('service.comments')"
                                :placeholder="$t('service.comments')"
                                :validation-name="$t('service.comments')"
                            />
                        </div>
                    </div>
                    SS {{ entity }}
                    <template v-if="false">
                        <!-- We can't use <FormulateErrors /> here like usual, because we're dealing with
                    two forms and only submitting the last one, which means errors with the first form
                    won't show. -->
                        <div class="row" v-if="Object.keys(errors).length">
                            <div class="col-12 text-danger">
                                <h4>{{ $t("service.errors") }}</h4>
                                <ul>
                                    <li
                                        v-for="(item, prop) in errors"
                                        :key="prop"
                                    >
                                        {{ prop }}:
                                        <ul>
                                            <li
                                                v-for="(item2, prop2) in item"
                                                :key="prop2"
                                            >
                                                {{ prop2 }}:
                                                <ul>
                                                    <li
                                                        v-for="(item3,
                                                        i) in item2"
                                                        :key="i"
                                                    >
                                                        {{ item3 }}
                                                    </li>
                                                </ul>
                                            </li>
                                        </ul>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </template>
                    <div class="row mt-4">
                        <div class="col text-right">
                            <base-button
                                type="secondary"
                                :outline="true"
                                @click="$emit('close')"
                                >{{ $t("cancel") }}</base-button
                            >
                            <base-button
                                @click="saveForm"
                                type="primary"
                                :loading="saving"
                                >{{ $t("save") }}</base-button
                            >
                        </div>
                    </div>
                </FormulateForm>
            </template>
        </template>
    </modal>
</template>

<style lang="scss">
.finalized-elems {
    padding-left: 45px;
    border-left: 8px solid $primary;
    margin-bottom: 20px;
}
</style>
